import { zodResolver } from '@hookform/resolvers/zod';
import { type RequestCreate, RequestCreateSchema } from '@gestion-permisos/contracts';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { type ControllerRenderProps, type FieldPath, useController, useForm, useWatch } from 'react-hook-form';
import { Link } from 'react-router-dom';
import type { z } from 'zod';
import { ApiProblem } from '../api/problem.js';
import { buttonClasses } from '../components/button-styles.js';
import { type ChoiceOption, ChoiceList } from '../components/choice-list.js';
import { CARD_SURFACE } from '../components/card.js';
import { ProblemMessage } from '../components/problem-message.js';
import { timeSlots, useCalendarSettings, useHolidays, usePermissionTypes } from './catalog-api.js';
import { DOCUMENT_ACCEPT, documentProblem } from './request-document.js';

type FormInput = z.input<typeof RequestCreateSchema>;
type FieldName = FieldPath<FormInput>;

export const EMPTY_REQUEST: FormInput = { permissionTypeId: '', permissionDate: '', departureTime: '', returnTime: '', reason: '' };

const FIELDS: readonly FieldName[] = ['permissionTypeId', 'permissionDate', 'departureTime', 'returnTime', 'reason'];

/** Mensajes de la validación de **forma** (esquema del contrato). Las reglas de negocio las da el servidor. */
const SHAPE_MESSAGES: Record<FieldName, string> = {
  permissionTypeId: 'Selecciona un tipo de permiso.',
  permissionDate: 'Indica la fecha del permiso.',
  departureTime: 'Selecciona la hora de salida.',
  returnTime: 'Selecciona una hora de regreso posterior a la de salida.',
  reason: 'Escribe el motivo (hasta 2000 caracteres).',
};

const isField = (value: string): value is FieldName => (FIELDS as readonly string[]).includes(value);

/** Errores del servidor con `path` `body.<campo>` → campo del formulario; el resto queda en el mensaje general. */
function fieldErrorsOf(error: unknown): [FieldName, string][] {
  if (!(error instanceof ApiProblem)) return [];
  return error.errors.flatMap((item) => {
    const field = item.path.startsWith('body.') ? item.path.slice('body.'.length) : '';
    return isField(field) ? [[field, item.message] as [FieldName, string]] : [];
  });
}

export interface RequestFormProps {
  readonly initial: FormInput;
  readonly submitLabel: string;
  readonly pending: boolean;
  /** Error de la última operación: se muestra tal cual (Problem Details) y por campo cuando trae `body.…`. */
  readonly error: unknown;
  readonly notice?: string | undefined;
  /** Guardar. En el alta llega también el documento elegido, si lo hay (ya comprobada su forma). */
  readonly onSubmit: (values: RequestCreate, file?: File) => void;
  /**
   * «Enviar para aprobación» (solo el alta): misma validación de forma que guardar. Sin él, el pie solo tiene
   * el botón de guardar y no hay campo de documento (la edición).
   */
  readonly onSend?: ((values: RequestCreate, file?: File) => void) | undefined;
}

const DOCUMENT_INPUT_ID = 'documentFile';
const DOCUMENT_NOTICE_ID = 'documentFile-notice';
const DOCUMENT_HELP_ID = 'documentFile-help';
const DOCUMENT_ERROR_ID = 'documentFile-error';

/**
 * Formulario de solicitud (W3). Tipos activos, tramos de `calendar/settings` como texto y festivos solo
 * como aviso. No calcula horas, solapamientos ni festivos: `requestedHours` y las reglas son del servidor.
 * Con el aspecto de la Power App (`src/pages/new-request-page.tsx`): una tarjeta con «Datos del permiso» y
 * «Detalle y justificación», y en el pie «Salir sin guardar», el botón de guardar (el `submit`: Enter guarda) y,
 * si se pasa `onSend`, «Enviar para aprobación». Sin duración calculada.
 *
 * En el alta (con `onSend`), si el tipo admite documento, se elige aquí y se queda en el navegador: no es un
 * campo de `RequestCreate`. Se comprueba después de la forma de la solicitud (`documentProblem`) y viaja con
 * cualquiera de los dos botones; cambiar de tipo lo descarta. «Enviar para aprobación» con un tipo que lo
 * exige pide el archivo; «Guardar borrador» puede seguir sin él (se adjunta luego en el detalle).
 */
export function RequestForm({ initial, submitLabel, pending, error, notice, onSubmit, onSend }: RequestFormProps) {
  const types = usePermissionTypes();
  const settings = useCalendarSettings();
  const form = useForm<FormInput, unknown, RequestCreate>({ resolver: zodResolver(RequestCreateSchema), defaultValues: initial });
  const { register, handleSubmit, formState, setError, reset, control } = form;
  const [file, setFile] = useState<File | undefined>(undefined);
  const [fileError, setFileError] = useState<string | undefined>(undefined);
  const fileInput = useRef<HTMLInputElement>(null);
  // Los campos se registran en el orden de la pantalla (los selectores con `useController`), para que el primer
  // error reciba el foco como con los `<select>`: tipo, fecha, salida, regreso y motivo.
  const typeField = useController({ control, name: 'permissionTypeId' }).field;
  const dateField = register('permissionDate');
  const departureField = useController({ control, name: 'departureTime' }).field;
  const returnField = useController({ control, name: 'returnTime' }).field;

  // Una versión nueva del recurso (p. ej., tras un 412) vuelve a cargar los valores en el formulario.
  useEffect(() => reset(initial), [initial, reset]);

  useEffect(() => {
    for (const [field, message] of fieldErrorsOf(error)) setError(field, { type: 'server', message });
  }, [error, setError]);

  const permissionDate = useWatch({ control, name: 'permissionDate' });
  const permissionTypeId = useWatch({ control, name: 'permissionTypeId' });
  const holidays = useHolidays(permissionDate);
  const holiday = holidays.data?.find((item) => item.active && item.date === permissionDate);
  const selectedType = types.data?.items.find((type) => type.id === permissionTypeId);

  const catalogError = types.error ?? settings.error;
  if (catalogError) return <ProblemMessage error={catalogError} />;
  if (!types.isSuccess || !settings.isSuccess) return <output className="block text-muted-foreground">Cargando el formulario…</output>;

  const describedBy = (field: FieldName) =>
    formState.errors[field] ? { 'aria-invalid': true, 'aria-describedby': `${field}-error` } : {};
  // Mientras se valida o hay una llamada en curso, ninguno de los dos botones admite otra pulsación.
  const busy = pending || formState.isSubmitting;
  const offersDocument = onSend !== undefined && selectedType?.allowsDocument === true;
  const requiresDocument = offersDocument && selectedType?.requiresDocument === true;
  /**
   * Tras la forma de la solicitud, el documento: el elegido debe pasar `documentProblem`, y `required` (enviar
   * con un tipo que lo exige) pide uno. Si falla, no hay llamada: el error queda en el campo, que recibe el foco.
   */
  const withDocument = (next: (values: RequestCreate, file?: File) => void, required: boolean) => (values: RequestCreate) => {
    const problem = file !== undefined || required ? documentProblem(file) : undefined;
    setFileError(problem);
    if (problem !== undefined) {
      fileInput.current?.focus();
      return;
    }
    next(values, file);
  };
  // Si falla la forma de la solicitud, no se mira el archivo.
  const clearFileError = () => setFileError(undefined);
  const slots = timeSlots(settings.data.workdayStart, settings.data.workdayEnd, settings.data.slotMinutes);
  const message = (field: FieldName) => {
    const fieldError = formState.errors[field];
    if (!fieldError) return undefined;
    return fieldError.type === 'server' ? fieldError.message : SHAPE_MESSAGES[field];
  };
  /** Selector controlado por el formulario: valida igual que el `<select>` registrado (mismo `id`, mismo valor). */
  const choice = (
    field: ControllerRenderProps<FormInput, 'permissionTypeId' | 'departureTime' | 'returnTime'>,
    options: readonly ChoiceOption[],
    onChange: (value: string) => void = field.onChange,
  ) => (
    <ChoiceList
      id={field.name}
      ref={field.ref}
      value={field.value}
      options={[{ value: '', label: 'Selecciona…' }, ...options]}
      onChange={onChange}
      onBlur={field.onBlur}
      {...describedBy(field.name)}
      className={INPUT}
    />
  );
  const slotOptions = (items: string[]) => items.map((slot) => ({ value: slot, label: slot }));

  return (
    <form noValidate onSubmit={handleSubmit(withDocument(onSubmit, false), clearFileError)} className={`overflow-hidden shadow-md ${CARD_SURFACE}`}>
      {notice || (error !== undefined && error !== null) ? (
        <div className="space-y-3 border-b border-border p-5 sm:px-7">
          {notice ? (
            <output className="block rounded-md bg-status-pending p-3 text-sm text-status-pending-foreground">{notice}</output>
          ) : null}
          {error !== undefined && error !== null ? <ProblemMessage error={error} /> : null}
        </div>
      ) : null}

      <div className="grid lg:grid-cols-2">
        <section aria-labelledby="datos-del-permiso" className="space-y-6 border-b border-border p-5 sm:p-7 lg:border-r lg:border-b-0">
          <div>
            <h2 id="datos-del-permiso" className="text-lg font-semibold">
              Datos del permiso
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">Selecciona el tipo, la fecha y el horario solicitado.</p>
          </div>

          <Field id="permissionTypeId" label="Tipo de permiso" error={message('permissionTypeId')}>
            {choice(
              typeField,
              types.data.items.map((type) => ({ value: type.id, label: type.name })),
              (value) => {
                // Otro tipo: el archivo elegido se descarta (el campo se vuelve a montar vacío).
                setFile(undefined);
                setFileError(undefined);
                typeField.onChange(value);
              },
            )}
          </Field>

          <Field id="permissionDate" label="Fecha del permiso" error={message('permissionDate')}>
            <input id="permissionDate" type="date" {...dateField} {...describedBy('permissionDate')} className={INPUT} />
            {holiday ? (
              <p className="mt-1 text-sm text-status-pending-foreground">Festivo: {holiday.name}. El servidor decidirá si la fecha es válida.</p>
            ) : null}
          </Field>

          <div className="grid gap-5 sm:grid-cols-2">
            <Field id="departureTime" label="Hora de salida" error={message('departureTime')}>
              {choice(departureField, slotOptions(slots.slice(0, -1)))}
            </Field>
            <Field id="returnTime" label="Hora de regreso" error={message('returnTime')}>
              {choice(returnField, slotOptions(slots.slice(1)))}
            </Field>
          </div>
        </section>

        <section aria-labelledby="detalle-y-justificacion" className="space-y-6 bg-muted p-5 text-foreground sm:p-7">
          <div>
            <h2 id="detalle-y-justificacion" className="text-lg font-semibold">
              Detalle y justificación
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">Explica el motivo de la solicitud. Las horas las calcula el servidor al guardar.</p>
          </div>

          <Field id="reason" label="Motivo" error={message('reason')}>
            <textarea id="reason" rows={5} {...register('reason')} {...describedBy('reason')} className={`${INPUT} min-h-32 resize-y`} />
          </Field>

          {offersDocument ? (
            <div>
              <label htmlFor={DOCUMENT_INPUT_ID} className="text-sm font-medium">
                Documento justificativo
              </label>
              <p id={DOCUMENT_NOTICE_ID} className="mt-2 rounded-md border border-border bg-card p-3 text-sm text-muted-foreground">
                {requiresDocument
                  ? 'Este tipo exige un documento justificativo. Puedes adjuntarlo ahora o guardar el borrador y adjuntarlo en el detalle antes de enviarlo.'
                  : 'Puedes adjuntar un documento justificativo. No es obligatorio.'}
              </p>
              <input
                key={permissionTypeId}
                ref={fileInput}
                id={DOCUMENT_INPUT_ID}
                type="file"
                accept={DOCUMENT_ACCEPT}
                onChange={(event) => {
                  setFile(event.target.files?.[0]);
                  setFileError(undefined);
                }}
                aria-invalid={fileError !== undefined}
                aria-describedby={[DOCUMENT_NOTICE_ID, DOCUMENT_HELP_ID, ...(fileError !== undefined ? [DOCUMENT_ERROR_ID] : [])].join(' ')}
                className="mt-2 block w-full text-sm text-foreground file:mr-3 file:rounded-md file:border file:border-border file:bg-card file:px-3 file:py-1.5 file:text-sm file:font-medium"
              />
              <p id={DOCUMENT_HELP_ID} className="mt-1 text-sm text-muted-foreground">
                PDF, JPEG o PNG, hasta 10 MB
              </p>
              {fileError !== undefined ? (
                <p id={DOCUMENT_ERROR_ID} className="mt-1 text-sm text-destructive">
                  {fileError}
                </p>
              ) : null}
            </div>
          ) : null}
        </section>
      </div>

      <footer className="flex flex-col-reverse gap-3 border-t border-border bg-card p-5 sm:flex-row sm:items-center sm:justify-end sm:p-6">
        <Link to="/mis-solicitudes" className={buttonClasses('ghost')}>
          Salir sin guardar
        </Link>
        <button type="submit" disabled={busy} className={buttonClasses(onSend ? 'outline' : 'default')}>
          {submitLabel}
        </button>
        {onSend ? (
          <button
            type="button"
            disabled={busy}
            className={buttonClasses()}
            onClick={() => void handleSubmit(withDocument(onSend, requiresDocument), clearFileError)()}
          >
            Enviar para aprobación
          </button>
        ) : null}
      </footer>
    </form>
  );
}

const INPUT =
  'mt-2 block w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-card-foreground outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40';
/** Botón principal de siempre: lo usan la revisión del aprobador y la analítica (el formulario usa `buttonClasses()`). */
export const PRIMARY_BUTTON = 'rounded-md bg-slate-900 px-4 py-2 text-white hover:bg-slate-700 disabled:opacity-50';

function Field({ id, label, error, children }: { id: string; label: string; error: string | undefined; children: ReactNode }) {
  return (
    <div>
      <label htmlFor={id} className="text-sm font-medium">
        {label}
      </label>
      {children}
      {error ? (
        <p id={`${id}-error`} className="mt-1 text-sm text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}
